export default function HeroSection() {
  return (
    <section id="hero" className="relative min-h-[600px] md:min-h-[700px] flex items-center bg-background-50 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-100/30 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent-100/20 rounded-full blur-3xl -translate-x-1/4 translate-y-1/4"></div>
      </div>

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          {/* Left Column - Text */}
          <div className="flex-1 w-full flex flex-col items-start">
            {/* Topic Tags before H1 */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">国漫</span>
              <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">日漫</span>
              <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">韩漫</span>
              <span className="px-3 py-1 text-xs font-medium bg-primary-100/70 text-primary-600 rounded-full whitespace-nowrap">免费下拉式</span>
            </div>

            {/* H1 */}
            <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground-950 leading-tight mb-3">
              九妖漫画网
              <br className="sm:hidden" />
              <span className="text-primary-500"> - </span>
              <span className="text-primary-500">免费漫画下拉式追更平台</span>
            </h1>

            {/* Slogan after H1 */}
            <p className="font-heading text-lg md:text-xl text-foreground-600 font-semibold mb-6">
              好看的漫画，随时随地免费看 · 每日更新，精彩不停
            </p>

            {/* Intro paragraphs */}
            <div className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-xl mb-6 space-y-2">
              <p>
                九妖漫画网汇聚海量免费漫画资源，涵盖<strong>国漫推荐</strong>、<strong>日漫精选</strong>、<strong>韩漫热门</strong>等全品类漫画，采用极速下拉式连续阅读模式，让您告别翻页等待，沉浸在流畅的漫画世界中。
              </p>
              <p>
                平台每日同步更新最新章节，覆盖热血、恋爱、玄幻、都市、悬疑、搞笑等多种题材，无论您是新读者还是资深漫迷，都能在这里找到心仪的作品。
              </p>
            </div>

            {/* Stats / Status */}
            <div className="flex flex-wrap items-center gap-5 md:gap-8 mb-8">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-book-2-line text-primary-600"></i>
                </span>
                <div>
                  <div className="text-lg font-bold text-foreground-950 whitespace-nowrap">30,000+</div>
                  <div className="text-xs text-foreground-500 whitespace-nowrap">漫画资源</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-flashlight-line text-accent-600"></i>
                </span>
                <div>
                  <div className="text-lg font-bold text-foreground-950 whitespace-nowrap">每日更新</div>
                  <div className="text-xs text-foreground-500 whitespace-nowrap">持续追更</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-user-smile-line text-secondary-600"></i>
                </span>
                <div>
                  <div className="text-lg font-bold text-foreground-950 whitespace-nowrap">500万+</div>
                  <div className="text-xs text-foreground-500 whitespace-nowrap">活跃漫迷</div>
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="#cards"
                className="px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                开始阅读 <i className="ri-arrow-right-line ml-1 align-middle"></i>
              </a>
              <a
                href="#topics"
                className="px-8 py-3 border-2 border-foreground-300 text-foreground-700 font-semibold text-sm rounded-full hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                探索话题 <i className="ri-compass-3-line ml-1 align-middle"></i>
              </a>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="flex-1 w-full max-w-lg lg:max-w-none flex justify-center lg:justify-end">
            <div className="relative w-full max-w-md lg:max-w-none aspect-[4/5] lg:aspect-[5/4] rounded-2xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Vibrant%20stylized%20manga%20illustration%20featuring%20colorful%20floating%20comic%20panels%20and%20speed%20lines%2C%20warm%20orange%20and%20coral%20tones%2C%20manga%20reading%20theme%20with%20abstract%20comic%20book%20elements%2C%20dynamic%20energy%20bursts%2C%20soft%20cream%20background%20gradient%2C%20modern%20Japanese%20manga%20art%20aesthetic%2C%20artistic%20flat%20illustration%20style%2C%20atmospheric%20lighting%20with%20warm%20glow%2C%20high%20detail%2C%20harmonious%20composition%2C%20editorial%20quality&width=1600&height=1200&seq=hero-manga-2026&orientation=landscape"
                alt="九妖漫画网 - 免费漫画下拉式阅读平台主视觉"
                className="w-full h-full object-cover object-top"
              />
              {/* Subtle overlay gradient for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/10 to-transparent pointer-events-none"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}